'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @primitive Sortable
 * ROLE: sortable list | ARIA: role="listbox" on container; each item role="option" aria-selected
 * KEYBOARD: Space activates drag on focused handle; ArrowUp/Down move; Space/Enter confirms; Escape cancels
 * FOCUS: drag handle receives focus per item; focus maintained through reorder
 * LIVE-REGION: DragAnnouncer (assertive) announces state changes
 * TOUCH A11Y: each item exposes a "Reorder" button (via ReorderButton) opening an inline position picker
 *             (up/down stepper) so AT touch users can reorder without pointer drag
 * RSC: CLIENT
 * WAI-APG: https://www.w3.org/WAI/ARIA/apg/patterns/listbox/
 * SUBSTRATE: @dnd-kit/react + @dnd-kit/dom (KeyboardSensor pinned to Space-activate / arrow-move / Enter-confirm / Escape-cancel)
 */
import { useId, useState, useCallback } from 'react';
import { DragDropProvider } from '@dnd-kit/react';
import { KeyboardSensor, PointerSensor } from '@dnd-kit/dom';
import { useSortable, isSortable } from '@dnd-kit/react/sortable';
import { DragAnnouncer } from '../DragAnnouncer/DragAnnouncer';
import styles from './Sortable.module.css';
const DEFAULT_SORTABLE_LABELS = {
    dragHandle: (label) => `Drag to reorder ${label}`,
    reorderButton: (label) => `Reorder ${label}`,
    list: 'Sortable list',
    picker: (label, position, total) => `Reorder ${label}: currently position ${position} of ${total}`,
    moveUp: (label) => `Move ${label} up`,
    moveDown: (label) => `Move ${label} down`,
    closePicker: 'Close reorder picker',
    reorderText: 'Reorder',
    doneText: 'Done',
};
// -------------------------------------------------------------------
// Keyboard sensor pinned to a11y-correct keybindings per Pillar 34
// -------------------------------------------------------------------
const keyboardSensor = KeyboardSensor.configure({
    keyboardCodes: {
        start: ['Space'],
        cancel: ['Escape'],
        end: ['Space', 'Enter'],
        up: ['ArrowUp'],
        down: ['ArrowDown'],
        left: ['ArrowLeft'],
        right: ['ArrowRight'],
    },
});
function SortableRow({ item, index, total, labels, onMove, renderItem, }) {
    const handleId = useId();
    const [pickerOpen, setPickerOpen] = useState(false);
    const { ref, handleRef, isDragging } = useSortable({
        id: item.id,
        index,
        data: { label: item.label },
    });
    const handleMoveUp = useCallback(() => {
        if (index > 0)
            onMove(index, index - 1);
    }, [index, onMove]);
    const handleMoveDown = useCallback(() => {
        if (index < total - 1)
            onMove(index, index + 1);
    }, [index, total, onMove]);
    const dragHandle = (_jsx("button", { ref: handleRef, id: handleId, tabIndex: 0, role: "button", "aria-label": labels.dragHandle(item.label), "aria-pressed": isDragging, className: styles.dragHandle, type: "button", children: _jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "currentColor", "aria-hidden": "true", focusable: "false", children: [_jsx("circle", { cx: "5", cy: "4", r: "1.5" }), _jsx("circle", { cx: "11", cy: "4", r: "1.5" }), _jsx("circle", { cx: "5", cy: "8", r: "1.5" }), _jsx("circle", { cx: "11", cy: "8", r: "1.5" }), _jsx("circle", { cx: "5", cy: "12", r: "1.5" }), _jsx("circle", { cx: "11", cy: "12", r: "1.5" })] }) }));
    // Touch a11y alternative: "Reorder" button opens an inline up/down picker.
    const reorderButton = (_jsxs("div", { className: styles.reorderWrapper, children: [_jsx("button", { type: "button", className: styles.reorderButton, "aria-label": labels.reorderButton(item.label), "aria-expanded": pickerOpen, onClick: () => setPickerOpen((o) => !o), children: labels.reorderText }), pickerOpen && (_jsxs("div", { role: "group", "aria-label": labels.picker(item.label, index + 1, total), className: styles.reorderPicker, children: [_jsx("button", { type: "button", className: styles.reorderStep, disabled: index === 0, "aria-label": labels.moveUp(item.label), onClick: handleMoveUp, children: "\u2191" }), _jsxs("span", { "aria-live": "polite", className: styles.reorderPosition, children: [index + 1, " / ", total] }), _jsx("button", { type: "button", className: styles.reorderStep, disabled: index === total - 1, "aria-label": labels.moveDown(item.label), onClick: handleMoveDown, children: "\u2193" }), _jsx("button", { type: "button", className: styles.reorderClose, "aria-label": labels.closePicker, onClick: () => setPickerOpen(false), children: labels.doneText })] }))] }));
    return (_jsx("li", { ref: ref, "aria-selected": isDragging, "data-dragging": isDragging || undefined, className: styles.item, children: renderItem(item, dragHandle, reorderButton) }));
}
// -------------------------------------------------------------------
// Public: Sortable component
// -------------------------------------------------------------------
/**
 * Convenience component wrapping `DragDropProvider` + per-item `useSortable`.
 * Includes `DragAnnouncer` for screen-reader support and a touch a11y
 * "Reorder" up/down picker on every item.
 *
 * Usage:
 * ```tsx
 * <Sortable
 *   items={items}
 *   onReorder={setItems}
 *   srStrings={{
 *     onPickUp: (item) => `Picked up ${item}. Use arrow keys to move; Space or Enter to drop; Escape to cancel.`,
 *     onMove: (item, pos, total) => `${item} is over position ${pos} of ${total}.`,
 *     onDrop: (item, pos) => `${item} was dropped at position ${pos}.`,
 *     onCancel: (item) => `Drag cancelled. ${item} returned to original position.`,
 *   }}
 * >
 *   {(item, dragHandle) => (
 *     <ListItem leading={dragHandle} title={item.label} />
 *   )}
 * </Sortable>
 * ```
 */
export function Sortable({ items, onReorder, children, srStrings, labels, className, }) {
    const resolvedLabels = { ...DEFAULT_SORTABLE_LABELS, ...labels };
    const handleReorder = useCallback((event) => {
        if (event.canceled)
            return;
        // @dnd-kit/react reorders the sortable OPTIMISTICALLY during the drag, so
        // by drag-end the drop `target` is stale/equal to the source and an
        // id-based lookup bails out (the move then snaps back). Read the sortable's
        // own indices instead: `initialIndex` is where it started, `index` is where
        // it ended — reproduce that move on our controlled array and commit it.
        const source = event.operation.source;
        if (source === null || !isSortable(source))
            return;
        const fromIndex = source.initialIndex;
        const toIndex = source.index;
        if (fromIndex === toIndex)
            return;
        const next = [...items];
        const [moved] = next.splice(fromIndex, 1);
        if (moved === undefined)
            return;
        next.splice(toIndex, 0, moved);
        onReorder(next);
    }, [items, onReorder]);
    const handleMove = useCallback((fromIndex, toIndex) => {
        if (fromIndex === toIndex)
            return;
        const next = [...items];
        const [moved] = next.splice(fromIndex, 1);
        if (moved === undefined)
            return;
        next.splice(toIndex, 0, moved);
        onReorder(next);
    }, [items, onReorder]);
    return (_jsxs(DragDropProvider, { onDragEnd: handleReorder, sensors: [PointerSensor, keyboardSensor], children: [_jsx(DragAnnouncer, { srStrings: srStrings, total: items.length }), _jsx("ul", { role: "listbox", "aria-label": resolvedLabels.list, className: className, children: items.map((item, index) => (_jsx(SortableRow, { item: item, index: index, total: items.length, labels: resolvedLabels, onMove: handleMove, renderItem: children }, item.id))) })] }));
}
//# sourceMappingURL=Sortable.js.map